import { type Paginated } from '@deque/pagination-utils';
import { apiRequest } from '../../api-client';
import { downloadBlob } from '../downloadBlob';

export type SecurityEventLogExportStatus =
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'failed';

export interface SecurityEventLogExport {
  exportId: string;
  enterpriseId: string;
  status: SecurityEventLogExportStatus;
  startDate: string;
  endDate: string;
  fileSize: number | null;
  eventCount: number | null;
  error: string | null;
  createdBy: string;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

const exportsEndpoint = (enterpriseId: string): string =>
  `/api-internal/enterprises/${enterpriseId}/security-event-log/exports`;

// Mirrors the backend's error_code for an unconfigured export storage (see
// services/axe-devtools/service/src/middleware/requireExportStorageConfigured.ts);
// distinguishes the expected "not configured" 503 from maintenance/proxy 503s.
export const EXPORTS_NOT_CONFIGURED_ERROR_CODE =
  'SECURITY_LOG_EXPORTS_NOT_CONFIGURED';

// Mirrors the Content-Disposition filename the backend sends on the
// filesystem-streaming path, so both download modes save the same name.
export const securityEventLogExportFilename = ({
  enterpriseId,
  startDate,
  endDate
}: Pick<
  SecurityEventLogExport,
  'enterpriseId' | 'startDate' | 'endDate'
>): string =>
  `security-events-${enterpriseId}-${startDate}-${endDate}.jsonl.gz`;

export function createSecurityEventLogExport({
  token,
  enterpriseId,
  start_date,
  end_date
}: {
  token: string;
  enterpriseId: string;
  start_date: string;
  end_date: string;
}): Promise<SecurityEventLogExport> {
  return apiRequest<SecurityEventLogExport>({
    method: 'POST',
    endpoint: exportsEndpoint(enterpriseId),
    token,
    body: { start_date, end_date }
  }) as Promise<SecurityEventLogExport>;
}

export function listSecurityEventLogExports({
  token,
  enterpriseId,
  page,
  per_page
}: {
  token: string;
  enterpriseId: string;
  page?: number;
  per_page?: number;
}): Promise<Paginated<SecurityEventLogExport[]>> {
  return apiRequest<SecurityEventLogExport[]>({
    method: 'GET',
    endpoint: exportsEndpoint(enterpriseId),
    token,
    page,
    per_page
  }) as Promise<Paginated<SecurityEventLogExport[]>>;
}

export async function deleteSecurityEventLogExport({
  token,
  enterpriseId,
  exportId
}: {
  token: string;
  enterpriseId: string;
  exportId: string;
}): Promise<void> {
  // Deliberately not `raw`: the JSON path already returns undefined for the
  // 204 response and, on failure, surfaces the backend's error message and
  // status instead of a generic "unexpected server error".
  await apiRequest<void>({
    method: 'DELETE',
    endpoint: `${exportsEndpoint(enterpriseId)}/${exportId}`,
    token
  });
}

/**
 * Triggers a browser download of a completed export. The backend returns either
 * a short-lived presigned URL (S3 — the browser pulls directly from S3) or the
 * gzip stream itself (filesystem backend); we handle both.
 */
export async function downloadSecurityEventLogExport({
  token,
  enterpriseId,
  exportId,
  filename
}: {
  token: string;
  enterpriseId: string;
  exportId: string;
  filename: string;
}): Promise<void> {
  const response = await apiRequest<Response>({
    method: 'GET',
    raw: true,
    endpoint: `${exportsEndpoint(enterpriseId)}/${exportId}/download`,
    token
  });

  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    const { downloadUrl } = (await response.json()) as { downloadUrl: string };
    triggerUrlDownload(downloadUrl, filename);
    return;
  }
  // Filesystem backend: the server streamed the file directly.
  downloadBlob(await response.blob(), filename);
}

// Anchor-click against an (often cross-origin, presigned) URL so the browser
// downloads it directly without navigating the SPA away.
function triggerUrlDownload(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  try {
    a.click();
  } finally {
    document.body.removeChild(a);
  }
}
